/**
 * Build the login path while preserving the tenant, so a forced return to the
 * login screen (logout, session expiry, 401) stays branded with the tenant's
 * theme/logo (`/login?tenant=<id>` — see CustomerThemeProvider / LoginPage).
 *
 * The tenant is taken from the current URL first (a branded entry point already
 * carries `?tenant=`), then from the stored authentication (`tri.authentication`),
 * which still holds the customer until logout clears it.
 *
 * A caller that was pushed off a page it wanted passes that page as
 * `redirectTo`; LoginPage sends the user back there once signed in.
 */

/** Login and logout are where the user already is — returning there would loop. */
const LOOPING_PATHS = ['/login', '/logout', '/signup'];

/**
 * The subset of `redirect` values that may be navigated to: paths inside this
 * app, and nothing else. `//evil.com` and `/\evil.com` start with a slash but
 * are read as protocol-relative URLs by the browser, so a bare `startsWith('/')`
 * check would hand an attacker an open redirect.
 */
export function safeRedirectTarget(value: string | null | undefined): string | null {
    if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
        return null;
    }

    const path = value.split('?')[0].split('#')[0];
    return LOOPING_PATHS.some((looping) => path === looping || path.startsWith(`${looping}/`)) ? null : value;
}

/** The page the user is on right now, in the form `redirect` expects. */
export function currentPathForRedirect(): string {
    return `${window.location.pathname}${window.location.search}`;
}

export function loginPathWithTenant(redirectTo?: string | null): string {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('tenant');
        let tenant = fromUrl && fromUrl !== 'null' ? fromUrl : null;

        if (!tenant) {
            const stored = localStorage.getItem('tri.authentication');
            if (stored) {
                const auth = JSON.parse(stored);
                const customer = auth?.customer;
                if (customer && customer !== 'null') {
                    tenant = String(customer);
                }
            }
        }

        const parameters = new URLSearchParams();
        if (tenant) {
            parameters.set('tenant', tenant);
        }

        const redirect = safeRedirectTarget(redirectTo);
        if (redirect) {
            parameters.set('redirect', redirect);
        }

        const query = parameters.toString();
        return query ? `/login?${query}` : '/login';
    } catch {
        return '/login';
    }
}
